import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';

const StatusContainer = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.shape.borderRadius}px;
  padding: ${theme.spacing(3)};
  margin-bottom: ${theme.spacing(3)};
  box-shadow: ${theme.elevation[2]};

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing(4)};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing(3)};
  }
`;

const StatusItem = styled.div`
  text-align: center;
  padding: ${theme.spacing(2)};
  border-radius: ${theme.shape.borderRadius}px;
  background: ${theme.colors.surfaceVariant};
`;

const StatusLabel = styled.div`
  font-size: ${theme.typography.overline.fontSize};
  color: ${theme.colors.onSurfaceVariant};
  margin-bottom: ${theme.spacing(1)};
  font-weight: ${theme.typography.overline.fontWeight};
  text-transform: ${theme.typography.overline.textTransform};
  letter-spacing: ${theme.typography.overline.letterSpacing};
`;

const StatusValue = styled.div<{ status?: 'open' | 'closed' }>`
  font-size: ${theme.typography.h4.fontSize};
  font-weight: ${theme.typography.h4.fontWeight};
  color: ${props => props.status === 'open' ? theme.colors.success : 
          props.status === 'closed' ? theme.colors.error : theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing(1)};

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const LocationInfo = styled.div`
  background: ${theme.colors.surfaceVariant};
  border-radius: ${theme.shape.borderRadius}px;
  padding: ${theme.spacing(2)};
  margin-top: ${theme.spacing(3)};
  text-align: center;
  border-left: 3px solid ${theme.colors.primary};
`;

const LocationText = styled.div`
  font-size: ${theme.typography.h6.fontSize};
  font-weight: ${theme.typography.h6.fontWeight};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing(0.5)};
`;

const LocationDetails = styled.div`
  font-size: ${theme.typography.body2.fontSize};
  color: ${theme.colors.onSurfaceVariant};
  font-weight: ${theme.typography.body2.fontWeight};
`;

const PaymentInfo = styled.div`
  margin-top: ${theme.spacing(2)};
  padding: ${theme.spacing(2)};
  background: ${theme.colors.surfaceVariant};
  border-radius: ${theme.shape.borderRadius}px;
  border-left: 3px solid ${theme.colors.warning};
`;

const PaymentText = styled.div`
  font-size: ${theme.typography.body2.fontSize};
  color: ${theme.colors.onSurface};
  font-weight: ${theme.typography.body2.fontWeight};
  text-align: center;
`;

const ServiceStatus: React.FC = () => {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const isServiceOpen = () => {
    const hour = currentTime.getHours();
    return (hour >= 5 && hour < 23) || (hour === 23 && currentTime.getMinutes() <= 30);
  };

  const getNextServiceTime = () => {
    const hour = currentTime.getHours();
    if (hour < 5) return t('opensAt');
    if (hour >= 23 && currentTime.getMinutes() > 30) return t('tomorrow');
    if (hour >= 23) return t('closesAt');
    return t('openUntil');
  };

  return (
    <StatusContainer>
      <StatusGrid>
        <StatusItem>
          <StatusLabel>{t('bistroStatus')}</StatusLabel>
          <StatusValue status={isServiceOpen() ? 'open' : 'closed'}>
            {isServiceOpen() ? t('open') : t('closed')}
          </StatusValue>
        </StatusItem>
        <StatusItem>
          <StatusLabel>{t('serviceHoursLabel')}</StatusLabel>
          <StatusValue>{getNextServiceTime()}</StatusValue>
        </StatusItem>
      </StatusGrid>

      <LocationInfo>
        <LocationText>{t('bistroLocation')}: {t('car26Middle')}</LocationText>
        <LocationDetails>{t('lookForStaff')}</LocationDetails>
      </LocationInfo>

      <PaymentInfo>
        <PaymentText>{t('cashlessOnly')}</PaymentText>
      </PaymentInfo>
    </StatusContainer>
  );
};

export default ServiceStatus;